import {
    BarChart3,
    History,
    LayoutGrid,
    Maximize2,
    Minimize2,
    Pencil,
    Plus,
    Save,
    Sparkles,
    Table2,
} from 'lucide-react'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import type { BoardCardType, BoardVersionItem } from '~/api/types'

// w/h = 추가 시 기본 크기, minW/minH = 리사이즈·정규화 최소 크기.
// 최소 크기는 카드 내용(제목 헤더 + 본문)이 겹치지 않고 들어가는 하한이다
// (통계=제목·최근값·통계행 4행, 차트=축·플롯 영역 확보). rowHeight=40 기준.
export const CARD_TYPES: {
    type: BoardCardType
    label: string
    w: number
    h: number
    minW: number
    minH: number
}[] = [
    { type: 'timeseries', label: '시계열', w: 6, h: 6, minW: 4, minH: 4 },
    { type: 'stat', label: '통계', w: 3, h: 4, minW: 3, minH: 4 },
    { type: 'pie', label: '판정 분포', w: 3, h: 5, minW: 3, minH: 4 },
    { type: 'table', label: '표', w: 4, h: 6, minW: 3, minH: 4 },
    { type: 'text', label: '텍스트', w: 4, h: 4, minW: 2, minH: 2 },
]
export const TYPE_LABEL: Record<string, string> = Object.fromEntries(
    CARD_TYPES.map((t) => [t.type, t.label])
)

// 카드 타입별 최소 크기(격자 칸). 알 수 없는 타입은 보수적 하한.
export function cardMinSize(type: string): { minW: number; minH: number } {
    const meta = CARD_TYPES.find((t) => t.type === type)
    return meta ? { minW: meta.minW, minH: meta.minH } : { minW: 2, minH: 2 }
}

/** 분석보드 상단 툴바 — AI/편집/전체화면 토글 + 카드추가·버전 드롭다운 + 저장. */
export function BoardToolbar({
    version,
    dirty,
    fullscreen,
    edit,
    aiOpen,
    onToggleAi,
    onToggleEdit,
    onToggleFullscreen,
    addOpen,
    onToggleAdd,
    onCloseAdd,
    onAddCard,
    verOpen,
    versions,
    onToggleVersions,
    onCloseVersions,
    onRollback,
    saving,
    onSave,
}: {
    version: number
    dirty: boolean
    fullscreen: boolean
    edit: boolean
    aiOpen: boolean
    onToggleAi: () => void
    onToggleEdit: () => void
    onToggleFullscreen: () => void
    addOpen: boolean
    onToggleAdd: () => void
    onCloseAdd: () => void
    onAddCard: (type: BoardCardType) => void
    verOpen: boolean
    versions: BoardVersionItem[]
    onToggleVersions: () => void
    onCloseVersions: () => void
    onRollback: (version: number) => void
    saving: boolean
    onSave: () => void
}) {
    return (
        <div
            className={cn(
                'border-border bg-card flex flex-wrap items-center gap-2 rounded-md border',
                fullscreen ? 'px-2 py-1' : 'p-2'
            )}>
            <LayoutGrid className='text-primary ml-1 size-4' />
            <span className='text-foreground text-sm font-semibold'>
                분석보드
            </span>
            <span className='text-muted-foreground text-xs'>
                v{version} {dirty ? '· 저장 안 됨' : ''}
            </span>

            <div className='ml-auto flex items-center gap-1.5'>
                <Button
                    variant={aiOpen ? 'default' : 'outline'}
                    size='sm'
                    title='AI 빌더'
                    onClick={onToggleAi}>
                    <Sparkles />
                    {!fullscreen && 'AI 빌더'}
                </Button>
                <Button
                    variant='outline'
                    size='sm'
                    title={edit ? '보기' : '편집'}
                    onClick={onToggleEdit}>
                    <Pencil />
                    {!fullscreen && (edit ? '보기' : '편집')}
                </Button>
                <Button
                    variant='outline'
                    size='sm'
                    title={fullscreen ? '전체화면 해제' : '전체화면'}
                    onClick={onToggleFullscreen}>
                    {fullscreen ? <Minimize2 /> : <Maximize2 />}
                    {!fullscreen && '전체화면'}
                </Button>

                {edit ? (
                    <div className='relative'>
                        <Button size='sm' onClick={onToggleAdd}>
                            <Plus />
                            카드 추가
                        </Button>
                        {addOpen ? (
                            <>
                                <div
                                    className='fixed inset-0 z-10'
                                    onClick={onCloseAdd}
                                />
                                <div className='border-border bg-card absolute right-0 z-20 mt-1 w-40 rounded-md border p-1 shadow-lg'>
                                    {CARD_TYPES.map((t) => (
                                        <button
                                            key={t.type}
                                            onClick={() => onAddCard(t.type)}
                                            className='hover:bg-muted/60 text-foreground flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm'>
                                            {t.type === 'table' ? (
                                                <Table2 className='size-3.5' />
                                            ) : (
                                                <BarChart3 className='size-3.5' />
                                            )}
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : null}
                    </div>
                ) : null}

                <div className='relative'>
                    <Button
                        variant='outline'
                        size='sm'
                        title='버전'
                        onClick={onToggleVersions}>
                        <History />
                        {!fullscreen && '버전'}
                    </Button>
                    {verOpen ? (
                        <>
                            <div
                                className='fixed inset-0 z-10'
                                onClick={onCloseVersions}
                            />
                            <div className='border-border bg-card absolute right-0 z-20 mt-1 max-h-72 w-64 overflow-y-auto rounded-md border p-1 shadow-lg'>
                                {versions.length === 0 ? (
                                    <p className='text-muted-foreground px-2.5 py-2 text-xs'>
                                        저장된 버전이 없어요.
                                    </p>
                                ) : (
                                    versions.map((v) => (
                                        <div
                                            key={v.version}
                                            className='hover:bg-muted/60 flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5'>
                                            <span className='text-foreground min-w-0 truncate text-xs'>
                                                v{v.version} · {v.cardCount}
                                                카드{' '}
                                                {v.note ? `· ${v.note}` : ''}
                                            </span>
                                            <button
                                                onClick={() =>
                                                    onRollback(v.version)
                                                }
                                                className='text-primary shrink-0 text-xs hover:underline'>
                                                롤백
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    ) : null}
                </div>

                <Button
                    size='sm'
                    onClick={onSave}
                    title='저장'
                    loading={saving}
                    disabled={!dirty}>
                    {!saving && <Save />}
                    {!fullscreen && '저장'}
                </Button>
            </div>
        </div>
    )
}
